import { cn } from '@/lib/utils';
import {
  BillRowVm,
  ParticipantBillVm,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { AddManualLineParams } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import Toggle from '@/components/ui/Toggle.tsx';
import { Card } from '@/components/ui/Card.tsx';
import { SectionColumnHeader } from '@/features/dashboard/bookingChanges/components/primitives';
import BillSectionList from './BillSectionList';
import ParticipantBillFooter from './ParticipantBillFooter';

interface ParticipantBillViewProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  data: ParticipantBillVm;
  isEditing: boolean;
  onlyChanged: boolean;
  onOnlyChangedChange: (onlyChanged: boolean) => void;
  onRowAction: (row: BillRowVm) => void;
  /** Widok zna właściciela rachunku, więc sam dopina go do parametrów. */
  onAddManualRow?: (params: AddManualLineParams) => void;
  /** „Raty i wpłaty →" — raty żyją wyłącznie na ekranie rezerwacji. */
  onOpenInstallments: () => void;
  bookingCode: string;
  /** Kafel osoby na ekranie rezerwacji nosi własny filtr w belce szkicu. */
  showOnlyChangedToggle?: boolean;
  /**
   * Wiersz udziału w rezerwacji. Na ekranie uczestnika prowadzi do rat;
   * w rozwiniętym kaflu jest zbędny, bo raty stoją kilka kart niżej.
   */
  showShare?: boolean;
  /**
   * Widok wpuszczony w kafel osoby — karta gubi ramkę, promień i cień, bo
   * obudowuje ją już kafel.
   */
  embedded?: boolean;
}

/**
 * Ekran uczestnika (A1/A2) — wszystkie dane i cały rachunek jednej osoby w
 * jednej liście. Ten sam komponent wpada w rozwinięty kafel uczestnika na
 * ekranie rezerwacji, więc żadna pozycja nie występuje w systemie dwa razy.
 *
 * Podgląd i edycja mają identyczny układ: różni je widoczność przycisków,
 * kropki zmian, stare wartości i delta w stopce.
 */
const ParticipantBillView = ({
  data,
  isEditing,
  onlyChanged,
  onOnlyChangedChange,
  onRowAction,
  onAddManualRow,
  onOpenInstallments,
  bookingCode,
  showOnlyChangedToggle = true,
  showShare = true,
  embedded = false,
  className,
  ...props
}: ParticipantBillViewProps) => (
  <Card
    highlighted={isEditing}
    className={cn(
      'overflow-hidden',
      embedded && 'rounded-none border-0 shadow-none',
      className,
    )}
    {...props}
  >
    <div className="px-4 pt-0.5">
      <SectionColumnHeader>
        {isEditing && showOnlyChangedToggle && (
          <Toggle
            size="sm"
            label="Tylko zmienione"
            checked={onlyChanged}
            onCheckedChange={onOnlyChangedChange}
          />
        )}
      </SectionColumnHeader>

      <BillSectionList
        className="pb-0.5"
        sections={data.sections}
        isEditing={isEditing}
        onlyChanged={onlyChanged}
        onRowAction={onRowAction}
        onAddManualRow={(section) =>
          onAddManualRow?.({
            participantId: data.person.id,
            sectionId: section.id,
          })
        }
      />
    </div>

    <ParticipantBillFooter
      className="mt-[22px]"
      totals={data.totals}
      isEditing={isEditing}
      bookingCode={bookingCode}
      onOpenInstallments={onOpenInstallments}
      showShare={showShare}
    />
  </Card>
);

export default ParticipantBillView;
